// 2026-11-02: customer cancellation requires owner action proof. The legacy
// version-only submit is removed; this embed explains availability and keeps
// the subscription. Resume remains supported. Older cancellation notes below
// describe superseded behavior.
// ProductsTab — the Products & subscriptions section's content (My Account
// C1, task T7a; rewritten by the account-area redesign Task 7, Amendment
// 2026-10-02 F6/F8/F16/O3/O9 + ruling R4). Rendered by ProductsPanel (which
// owns the stores/RMA data — see that file's header) — a STANDALONE
// component so AccountCenter.tsx's edit stays a single import + render swap.
//
// ⚠️ AMENDED 2026-10-03 (account-area redesign, Task 7). The ORIGINAL
// presentation — one EmbedCard per owned product with a type-facet chip row,
// an inline cancel-confirm panel, a grey 64px image square, and the panel's
// two extension slots (renderProductActionSlot / renderProductStoreSlot) —
// is REPLACED by the amendment's: the Task-4 registry tab pair
// (SECTION_TABS.products, F16 — "Subscriptions" / "Hardware & one-time",
// partitioned on subscription PRESENCE, not product type), one EmbedTable
// row per product (SubscriptionsTable / OwnedProductsTable in this
// directory), the store select ON the row with a visible "Buy again" below
// it (F6), the cancel confirmation in an EmbedDialog (role="alertdialog"),
// and the R4 visible Cancel renewal / Resume subscription row text links
// (recouped to textSecondary — the placeholder-token colour failed AA; see
// SubscriptionRowLink). The STATE MACHINES are carried over unchanged: the
// load pipeline, the cancel/resume handlers and their §59 re-seeds,
// idempotency-key attachment, and the terminal-error refetch are the SAME
// code (byte-identical except where noted), which is what the name-based
// pins in ProductsTab.test.tsx prove.
//
// What happened to the two T7b extension points (rule 48 — the seam is
// replaced, so its doc-comment contract is replaced WITH it, in the same
// diff):
//   - `renderProductActionSlot` (per-card RMA button + gate text) → the ⋯
//     row menu's single "Request a return" item (hardware rows only; hidden
//     entirely while an open request exists) plus the Status column's rma
//     badge. ProductsPanel opens RmaRequestForm in an EmbedDialog when the
//     menu fires onRequestReturn.
//   - `renderProductStoreSlot` (per-card store selector) → the on-row
//     per-store split cell (TD-671 Task 8 retired the on-row store select and
//     its store PUT / create-store dialog).
//
// Data source: ONE read (see the 2026-10-27 amendment below) — GET
// /me/owned-products. The RMA data arrives through the panel-seam props;
// ProductsTab holds NO RMA state of its own.
//
// ⚠️ AMENDED 2026-10-10 (merge-review M2, TD-583). ORIGINAL: the tab loaded orders +
// catalog + subscriptions together and presented every control, assuming the section's gate
// (then manage-stores) implied all of its data. CURRENT: the section is gated by
// view-products and each panel degrades on ITS OWN area via the `access` prop
// (`productsAccess`): no view-orders -> a quiet note instead of the list (it is derived
// from orders), and none of the three reads is sent; no manage-subscriptions -> the
// subscriptions read is skipped and that tab shows a quiet note; no manage-stores -> no
// Move units action (the per-product Store select is retired); no manage-rma -> no return action or Status column. A denied read is never
// sent, so it can never surface as the load-error banner. The server's refusals remain the
// control.
//
// ⚠️ AMENDED 2026-10-27 (TD-671 Task 8). ORIGINAL (the two blocks above): rows
// were derived client-side from GET /me/orders + one detail per order + GET
// /catalog + GET /me/subscriptions, partitioned on subscription presence, and
// each row carried a per-product store select (ProductsPanel owned the PUT).
// CURRENT: ONE read, GET /me/owned-products (requires view-orders, so the
// "no view-orders -> quiet note, request never sent" rule is unchanged), and the
// SERVER decides the rows: a subscription item per subscription (R2) and a
// one-time item per product. The Subscriptions tab lists items whose
// `subscription` is present (the server nulls it without manage-subscriptions,
// where the tab keeps its quiet note); the other tab lists one-time items with
// their quantity and per-store split. The store select and its panel-seam props
// are gone (F11/F20); the cancel / resume handlers, their Idempotency-Key
// headers and §59 re-seeds are the same code, now re-seeding the ITEM's
// subscription. The product search matches the item's product name (the
// catalog description is no longer on the wire).
//
// ⚠️ AMENDED 2026-10-27 (TD-671 Task 13, rulings R9/R13, finding F12).
// ORIGINAL: the subscription row carried the R4 "Cancel renewal" link and the
// client decided Resume eligibility from cancel_at (isReversibleCancellation).
// CURRENT: cancel eligibility is the server's `cancel` action, shown as a small
// "Cancel subscription" link inside the owned-item detail dialog (opened by the
// row's "Details" button); Resume stays on the row and shows only when a
// `resume` action exists. The cancel confirm dialog, the POSTs, the
// Idempotency-Key headers and the §59 re-seed are unchanged. After a
// successful cancel/resume the item's `actions` change, so the tab re-reads
// GET /me/owned-products QUIETLY (no loading skeleton, errors ignored) rather
// than guessing the new action set client-side; until that read lands the
// re-seed has already removed the now-stale cancel/resume entries for that
// subscription, so no control is offered that the server has just retired.
// `help` actions are API-only (R13): nothing in this tab renders one.
//
// TD-671 Task 17 (R3/R5): a one-time row of a member with manage-stores carries
// a "Move" button that opens MoveUnitsDialog. A successful move re-seeds the row
// from the response item (rule 59, no refetch) through the read ticket, so a
// read that started before the move cannot put the old split back; a stale-
// bucket 422 refetches the list.
//
// Inline styles only — no Tailwind. No imports from portal/.

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  useEmbedTokens,
  EmbedBanner,
  EmbedButton,
  EmbedCard,
  EmbedDialog,
  EmbedInput,
  EmbedTabs,
  EmbedText,
} from "../../embed-shared/primitives";
import { embedApiFetch, ApiError } from "../../embed-shared/api-client";
import {
  idempotencyHeaders,
  idempotencyUserMessage,
  isIdempotencyCodeBug,
} from "../../embed-shared/idempotency-errors";
import { useCart } from "../../embed-shared/cart-context";
import type { Subscription } from "../../embed-shared/types";
import { SECTION_TABS, FULL_PRODUCTS_ACCESS, type ProductsAccess } from "./account-sections";
import { isTerminalSubscriptionError, matchesProductSearch } from "./owned-products";
import { listOwnedProducts, type OwnedItemWire, type OwnedLineWire } from "./owned-products-api";
import { OwnedItemDetailDialog } from "./OwnedItemDetailDialog";
import { MoveUnitsDialog } from "./MoveUnitsDialog";
import {
  SubscriptionsTable,
  type CancelState,
  type ResumeState,
  type SubscriptionsTableRow,
} from "./SubscriptionsTable";
import {
  OwnedProductsTable,
  type BuyAgainState,
  type OwnedProductsTableRow,
} from "./OwnedProductsTable";

/** Delays before the (at most three) re-reads that wait out replica lag. */
const REREAD_BACKOFF_MS = [500, 1000, 2000] as const;

/** The split a move response (or a read) shows for a one-time item. Assignments
 *  are sorted by store id first, so the comparison never depends on the server's
 *  ordering (it serves them by store name). */
export function moveSignature(item: OwnedItemWire): string {
  const assignments = [...(item.assignments ?? [])]
    .map((a) => [a.store.id, a.quantity] as const)
    .sort((x, y) => (x[0] < y[0] ? -1 : x[0] > y[0] ? 1 : 0));
  return JSON.stringify([item.unassigned_quantity ?? null, assignments]);
}

const SUBSCRIPTIONS_PATH ="/api/v1/marketplace/me/subscriptions";

// SECTION_TABS is a Partial record (every section COULD be tab-less); the
// products entry is static in this file's own constant table, so the ?? []
// fallback only satisfies the type — the two consumers below still handle an
// empty list ([0]?.id, and .map over nothing renders no tabs).
const PRODUCTS_TABS = SECTION_TABS.products ?? [];

/** Shown in place of the product list when the member lacks view-orders (the owned-products
 *  read is gated by it, because the data is order history). */
export const PRODUCTS_NO_ORDERS_MESSAGE =
  "Your role doesn't include access to this account's order history, which is where your products are listed from. Ask an account manager if you need it.";

/** Shown on the Subscriptions tab when the member lacks manage-subscriptions. */
export const PRODUCTS_NO_SUBSCRIPTIONS_MESSAGE =
  "Your role doesn't include access to this account's subscriptions. Ask an account manager if you need to see or change a renewal.";

export interface ProductsTabProps {
  /** Which of the section's data areas the member holds (TD-583). Omitted =
   *  everything permitted, the pre-TD-583 behaviour. */
  access?: ProductsAccess;
  /** The product's return-request status (open ?? latest ?? null) — gates
   *  the ⋯ menu and labels the Status badge. */
  rmaStatusFor: (productId: string) => string | null;
  /** "Request a return" fired from a row's ⋯ menu, with the trigger element
   *  focus must return to when the dialog closes, and the item's source lines
   *  (newest first) the dialog lets the customer choose between. */
  onRequestReturn: (productId: string, trigger: HTMLElement, lines: OwnedLineWire[]) => void;
}

export function ProductsTab({
  access = FULL_PRODUCTS_ACCESS,
  rmaStatusFor,
  onRequestReturn,
}: ProductsTabProps) {
  const t = useEmbedTokens();
  const { addLine } = useCart();

  const canOrders = access.orders;
  const canSubscriptions = access.subscriptions;

  // No view-orders -> nothing to load, so never start in the loading state.
  const [loading, setLoading] = useState(canOrders);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [items, setItems] = useState<OwnedItemWire[]>([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<string>(PRODUCTS_TABS[0]?.id ?? "subscriptions");

  const [buyAgainState, setBuyAgainState] = useState<Record<string, BuyAgainState>>({});
  // Keyed by subscription id (not product id) — the confirm/cancel action
  // operates on the subscription row.
  const [cancelState, setCancelState] = useState<Record<number, CancelState>>({});
  const [resumeState, setResumeState] = useState<Record<number, ResumeState>>({});

  // Read-ordering ticket: every read of the list (the full load AND the quiet
  // refresh) takes the next number BEFORE it starts, and applies its result only
  // if it still holds the newest one -- so a slow older response can never
  // overwrite a newer one. `mounted` stops state updates after unmount.
  const readSeq = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const [refreshFailed, setRefreshFailed] = useState(false);
  // Subscriptions re-seeded from a mutation whose actions no server read has
  // re-delivered yet (id -> the mutation's version): their row shows
  // "Updating…" (and "Active" + "Updating…" for a pending resume) rather than a
  // guessed "No longer reversible", and offers no Resume/Cancel.
  // The ref is the synchronous truth the re-read schedule decides on; the state
  // mirrors it for rendering. Always written through setPending.
  const [pendingSubs, setPendingSubsState] = useState<ReadonlyMap<number, number>>(new Map());
  const pendingRef = useRef<ReadonlyMap<number, number>>(new Map());
  const setPending = useCallback((next: ReadonlyMap<number, number>) => {
    pendingRef.current = next;
    setPendingSubsState(next);
  }, []);
  // One-time items re-seeded from a MOVE response whose split no read has
  // re-delivered yet (productId -> signature of the response item). Same job as
  // pendingSubs, for the same replica-lag reason, but a one-time item has no
  // `version` to compare, so the proof that a read caught up is that its split
  // EQUALS the move response's (moveSignature). Ref only: nothing renders from it.
  const pendingMovesRef = useRef<ReadonlyMap<string, string>>(new Map());
  const anyPending = useCallback(() => pendingRef.current.size > 0 || pendingMovesRef.current.size > 0, []);
  // Bounded backoff re-reads while a subscription or a move is still pending (replica lag).
  const rereadTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const clearRereads = useCallback(() => {
    rereadTimers.current.forEach(clearTimeout);
    rereadTimers.current = [];
  }, []);
  useEffect(() => clearRereads, [clearRereads]); // unmount cancels the schedule

  const load = useCallback(async () => {
    // TD-583: the owned-products read is gated by view-orders; without it there
    // is nothing to read, and sending the request would only produce a 403 banner.
    if (!canOrders) {
      setLoading(false);
      return;
    }
    const ticket = ++readSeq.current;
    clearRereads(); // a newer full load supersedes any scheduled re-read
    setLoading(true);
    setLoadError(null);
    try {
      const list = await listOwnedProducts();
      if (mounted.current && ticket === readSeq.current) {
        setItems(list);
        setPending(new Map());
        // A full load is user- or error-initiated: it takes the server's state.
        pendingMovesRef.current = new Map();
        setRefreshFailed(false);
      }
    } catch (err) {
      if (mounted.current && ticket === readSeq.current) {
        setLoadError(err instanceof ApiError ? err.message : "Failed to load your products");
      }
    } finally {
      // Always cleared: a read that was superseded by a quiet refresh must not
      // leave the skeleton up forever.
      if (mounted.current) setLoading(false);
    }
  }, [canOrders, clearRereads, setPending]);

  useEffect(() => {
    load();
  }, [load]);

  // Quiet re-read after a cancel/resume: the item's `actions` are the server's
  // verdict, so they are re-read rather than guessed. No loading flag (that
  // would swap the whole tab for the skeleton and drop dialog/row state). The
  // re-seed has already dropped the subscription's stale actions, so a failed
  // read would leave no Resume/Cancel: retry once, and if that fails too keep
  // the list and say so (a non-blocking notice) instead of a silent dead end.
  // The list read is served from a replica, so under lag it can return the
  // PRE-mutation row: a fetched subscription older than the re-seeded one (a
  // lower `version`) never replaces it (§59/R10), and that subscription stays
  // pending until a read catches up.
  //
  // A read that leaves a subscription pending is not the end: re-read at
  // REREAD_BACKOFF_MS (500, 1000, 2000 -- at most 3 more reads) under the SAME
  // ticket and the same version-aware merge, stopping as soon as nothing is
  // pending. If they run out with something still pending, show the notice (the
  // re-seeded state stays). A newer load(), a new mutation or unmount clears
  // the timers.
  const applyRead = useCallback(
    (list: OwnedItemWire[]): number => {
      const fetchedVersion = (id: number) => list.find((f) => f.subscription?.id === id)?.subscription?.version;
      // A pending move is settled once a read's split equals the response's; until
      // then the LOCAL item stands (the read is pre-move, from a lagging replica).
      // A product absent from the read has nothing to compare: the read wins.
      const stillMoving = new Map(pendingMovesRef.current);
      for (const [productId, sig] of pendingMovesRef.current) {
        const f = list.find((x) => x.kind === "one_time" && x.product.id === productId);
        if (!f || moveSignature(f) === sig) stillMoving.delete(productId);
      }
      pendingMovesRef.current = stillMoving;
      setItems((prev) =>
        list.map((f) => {
          if (f.kind === "one_time" && stillMoving.has(f.product.id)) {
            const keep = prev.find((p) => p.kind === "one_time" && p.product.id === f.product.id);
            if (keep) return keep;
          }
          const local = f.subscription && prev.find((p) => p.subscription?.id === f.subscription?.id);
          return local && local.subscription && f.subscription && local.subscription.version > f.subscription.version
            ? local
            : f;
        }),
      );
      const stillPending = new Map([...pendingRef.current].filter(([id, v]) => (fetchedVersion(id) ?? v) < v));
      setPending(stillPending);
      return stillPending.size + stillMoving.size;
    },
    [setPending],
  );

  // True once any read of the CURRENT backoff schedule threw (a real read
  // failure, as opposed to a read that succeeded but did not yet agree).
  const rereadFailed = useRef(false);

  const scheduleRereads = useCallback(
    (ticket: number, index: number) => {
      if (index === 0) rereadFailed.current = false;
      if (index >= REREAD_BACKOFF_MS.length) {
        if (mounted.current && ticket === readSeq.current && anyPending()) {
          // The notice is for a read that FAILED, or a subscription whose
          // mutation no read has confirmed. A MOVE alone whose reads all
          // succeeded but disagreed needs none: the local item is the server's
          // own move response, not a guess.
          if (rereadFailed.current || pendingRef.current.size > 0) setRefreshFailed(true);
          // A split that never matched (another member's move, a newly shipped
          // order) must stop being guarded: the local item stays on screen, and
          // the NEXT read wins instead of being discarded forever.
          pendingMovesRef.current = new Map();
        }
        return;
      }
      const timer = setTimeout(async () => {
        if (!mounted.current || ticket !== readSeq.current) return;
        try {
          const list = await listOwnedProducts();
          if (!mounted.current || ticket !== readSeq.current) return;
          if (applyRead(list) === 0) {
            setRefreshFailed(false);
            return;
          }
        } catch {
          /* counts as one of the bounded reads */
          rereadFailed.current = true;
        }
        // The fetch may have settled after unmount or after a newer read took over.
        if (!mounted.current || ticket !== readSeq.current) return;
        scheduleRereads(ticket, index + 1);
      }, REREAD_BACKOFF_MS[index]);
      rereadTimers.current.push(timer);
    },
    [applyRead, anyPending],
  );

  const refresh = useCallback(async () => {
    clearRereads();
    const ticket = ++readSeq.current;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const list = await listOwnedProducts();
        if (mounted.current && ticket === readSeq.current) {
          const remaining = applyRead(list);
          setRefreshFailed(false);
          if (remaining > 0) scheduleRereads(ticket, 0);
        }
        return;
      } catch {
        /* retry once, then notify */
      }
    }
    if (mounted.current && ticket === readSeq.current) setRefreshFailed(true);
  }, [applyRead, clearRereads, scheduleRereads]);

  // The detail dialog is keyed, not stored, so a refresh re-renders it from the
  // newest item: "sub:<subscription id>" (a hidden subscription item:
  // "sub:hidden:<product id>") or "product:<product id>" for a one-time item.
  const [detail, setDetail] = useState<{ key: string; trigger: HTMLElement } | null>(null);

  // The tab partition (F16) is the item KIND the server decided: a subscription
  // item per subscription row (R2, so one product can fill several rows) and a
  // one-time item per product. A subscription item whose `subscription` is null
  // (no manage-subscriptions) is deliberately in neither tab: the Subscriptions
  // tab keeps its quiet note and a hidden subscription never poses as a
  // one-time purchase. Both partitions apply the same search filter.
  const subscriptionRows = useMemo<SubscriptionsTableRow[]>(
    () =>
      items.flatMap((item) =>
        item.kind === "subscription" && item.subscription != null && matchesProductSearch(item.product, searchQuery)
          ? [{ item, subscription: item.subscription, pending: pendingSubs.has(item.subscription.id) }]
          : [],
      ),
    [items, searchQuery, pendingSubs],
  );

  const ownedRows = useMemo<OwnedProductsTableRow[]>(
    () =>
      items.flatMap((item) =>
        item.kind === "one_time" && matchesProductSearch(item.product, searchQuery) ? [{ item }] : [],
      ),
    [items, searchQuery],
  );

  // The live subscriptions, for the cancel dialog's lookup.
  const subscriptions = useMemo(
    () => items.flatMap((i) => (i.subscription ? [i.subscription] : [])),
    [items],
  );

  // §59 re-seed target: replace the subscription carried by the item that owns it.
  // The item's cancel/resume actions described the OLD state, so they are dropped
  // with it; the quiet refresh that follows brings the server's new set.
  const reseedSubscription = useCallback((updated: Subscription) => {
    setItems((prev) =>
      prev.map((i) =>
        i.subscription?.id === updated.id
          ? {
              ...i,
              subscription: updated,
              actions: i.actions.filter(
                (a) => !((a.type === "cancel" || a.type === "resume") && a.subscription_id === updated.id),
              ),
            }
          : i,
      ),
    );
    setPending(new Map(pendingRef.current).set(updated.id, updated.version));
  }, [setPending]);

  // The Move dialog is keyed by product id, so a refresh re-renders it from the
  // newest item (a stale-bucket refetch changes the From options in place).
  const [moveDialog, setMoveDialog] = useState<{ productId: string; trigger: HTMLElement } | null>(null);
  const moveItem = useMemo(
    () =>
      moveDialog
        ? (items.find((i) => i.kind === "one_time" && i.product.id === moveDialog.productId) ?? null)
        : null,
    [moveDialog, items],
  );

  // The item vanished under an open dialog (a refresh retired it): close.
  useEffect(() => {
    if (moveDialog && !moveItem) setMoveDialog(null);
  }, [moveDialog, moveItem]);

  // Rule 59 for a move: the row takes the server's item as-is (no refetch). The
  // response goes through the read ticket: taking the next number supersedes any
  // read already in flight, so a late quiet refresh that started BEFORE the move
  // can never put the pre-move split back. A read that STARTS after the move can
  // still be served by a lagging replica, so the move also joins the bounded
  // re-read schedule (pendingMovesRef): the re-seeded item stands until a read
  // shows the same split. Any subscription re-read the bump cancelled is
  // rescheduled with it under the new ticket.
  // A 422 from the move route is primary-sourced proof that the local split is
  // wrong (the server judged the move against the primary): stop guarding that
  // product and refetch, so the replacement is accepted, not discarded again.
  const handleMoveStale = useCallback(
    (productId: string) => {
      const next = new Map(pendingMovesRef.current);
      next.delete(productId);
      pendingMovesRef.current = next;
      void refresh();
    },
    [refresh],
  );

  const handleMoved = useCallback(
    (moved: OwnedItemWire) => {
      const ticket = ++readSeq.current;
      clearRereads();
      setItems((prev) =>
        prev.map((i) => (i.kind === "one_time" && i.product.id === moved.product.id ? moved : i)),
      );
      // The re-seed stands until a read shows the same split (see pendingMovesRef);
      // the bounded re-reads run for it and for any subscription still pending.
      pendingMovesRef.current = new Map(pendingMovesRef.current).set(moved.product.id, moveSignature(moved));
      setRefreshFailed(false);
      scheduleRereads(ticket, 0);
    },
    [clearRereads, scheduleRereads],
  );

  // Keyed by KIND first: a subscription item whose `subscription` is null (the
  // member lacks manage-subscriptions) must not share `product:<id>` with the
  // one-time item of the same product — subscription items sort first, so the
  // lookup below would return the hidden one. Only the detail dialog uses this key.
  const itemKey = (item: OwnedItemWire): string =>
    item.kind === "one_time"
      ? `product:${item.product.id}`
      : `sub:${item.subscription?.id ?? `hidden:${item.product.id}`}`;

  const detailItem = useMemo(
    () => (detail ? (items.find((i) => itemKey(i) === detail.key) ?? null) : null),
    [detail, items],
  );

  // Buy again always adds a single unit with NO store (F20): a split one-time
  // row has no single store, and a subscription row's own store is not a
  // purchase choice. Everything else about the flow is unchanged: server-
  // enforced max_order_qty, the verbatim error surfaced.
  const handleBuyAgain = useCallback(
    async (productId: string) => {
      setBuyAgainState((prev) => ({ ...prev, [productId]: { status: "loading" } }));
      // max_order_qty (AC8) is enforced server-side; addLine() surfaces the
      // MAX_QTY_EXCEEDED message verbatim via its returned `error` (never
      // swallowed, never silently clamped).
      const result = await addLine(productId, 1);
      setBuyAgainState((prev) => ({
        ...prev,
        [productId]: result.ok
          ? { status: "success" }
          : { status: "error", error: result.error ?? "We couldn't add that item to your cart." },
      }));
    },
    [addLine],
  );

  const handleRequestCancel = useCallback((subscriptionId: number) => {
    setCancelState((prev) => ({ ...prev, [subscriptionId]: { confirming: true, loading: false, error: null } }));
  }, []);

  const handleDismissCancel = useCallback((subscriptionId: number) => {
    setCancelState((prev) => ({ ...prev, [subscriptionId]: { confirming: false, loading: false, error: null } }));
  }, []);

  // Owner cancellation now requires action-bound SMS/passkey verification.
  // This legacy embed has no such ceremony yet, so it never posts cancellation.

  // TD-389 — reverse a scheduled cancellation. No confirm step (nothing
  // destructive to guard) and the same §59 re-seed as cancel: the response
  // carries the cleared cancel_at and the bumped version, and assuming either
  // would desynchronise the next optimistic-locked write.
  const handleResume = useCallback(async (sub: Subscription) => {
    setResumeState((prev) => ({ ...prev, [sub.id]: { loading: true, error: null } }));
    try {
      const updated = await embedApiFetch<Subscription>(`${SUBSCRIPTIONS_PATH}/${sub.id}/resume`, {
        method: "POST",
        body: { version: sub.version },
        // bearerIdem route — the caller MUST attach the key or the server
        // returns 400 IDEMPOTENCY_KEY_REQUIRED (rule 79; TD-449 is what
        // happens when it does not).
        headers: idempotencyHeaders(),
      });
      reseedSubscription(updated);
      setResumeState((prev) => ({ ...prev, [sub.id]: { loading: false, error: null } }));
      void refresh();
    } catch (err) {
      if (err instanceof ApiError && isIdempotencyCodeBug(err.code)) {
        console.warn("[subscriptions] missing Idempotency-Key — code bug", err.code);
      }
      const msg =
        err instanceof ApiError
          ? (idempotencyUserMessage(err.code) ?? err.message)
          : "Failed to resume your subscription.";
      setResumeState((prev) => ({ ...prev, [sub.id]: { loading: false, error: msg } }));
      if (err instanceof ApiError && isTerminalSubscriptionError(err.code)) {
        void load();
      }
    }
  }, [load, refresh, reseedSubscription]);

  // The cancel confirm dialog rides the same state record the row link
  // wrote — one source of truth, no parallel "open" flag to desynchronise.
  const confirmingSubscription = useMemo(
    () => subscriptions.find((s) => cancelState[s.id]?.confirming) ?? null,
    [subscriptions, cancelState],
  );

  if (!canOrders) {
    return (
      <EmbedCard>
        <div role="status" data-testid="products-no-orders" style={{ textAlign: "center", padding: t.spacing.xl }}>
          <EmbedText tone="secondary">{PRODUCTS_NO_ORDERS_MESSAGE}</EmbedText>
        </div>
      </EmbedCard>
    );
  }

  if (loading) {
    return (
      <div data-testid="products-tab-loading" style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            aria-hidden="true"
            style={{
              height: 96,
              borderRadius: t.borderRadius,
              background: t.colors.disabledBg,
            }}
          />
        ))}
      </div>
    );
  }

  if (loadError) {
    return (
      <EmbedCard>
        <EmbedBanner tone="error">{loadError}</EmbedBanner>
        <div style={{ marginTop: t.spacing.md }}>
          <EmbedButton type="button" variant="secondary" onClick={load}>
            Retry
          </EmbedButton>
        </div>
      </EmbedCard>
    );
  }

  if (items.length === 0) {
    return (
      <EmbedCard>
        <div role="status" style={{ textAlign: "center", padding: t.spacing.xl }}>
          <div style={{ marginBottom: t.spacing.md }}>
            <EmbedText tone="secondary">
              You haven&apos;t purchased anything yet. Items you buy will appear here for quick
              reordering.
            </EmbedText>
          </div>
          <EmbedButton type="button" variant="secondary" size="sm" onClick={load}>
            Refresh
          </EmbedButton>
        </div>
      </EmbedCard>
    );
  }

  const rowCount = activeTab === "subscriptions" ? subscriptionRows.length : ownedRows.length;

  return (
    <div data-testid="products-tab" style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
      {/* DECISION (plan Step 3): no "Browse catalog" header action. It would
          render only when an onBrowseCatalog callback exists, and no caller
          passes one — AccountCenter does not route to the catalog. Revisit
          only if MarketplaceEmbed starts passing the callback. */}
      {refreshFailed && (
        // The Retry sits BESIDE the banner, not inside it: the banner is a
        // role="alert" live region, so a button in it would be re-announced with
        // every message (the ProductsPanel load-error precedent).
        <div data-testid="products-refresh-notice" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: t.spacing.sm }}>
          <div style={{ flex: "1 1 200px" }}>
            <EmbedBanner tone="error">Couldn&apos;t refresh your products.</EmbedBanner>
          </div>
          <EmbedButton type="button" variant="secondary" size="sm" onClick={load}>
            Retry
          </EmbedButton>
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: t.spacing.sm, alignItems: "center", justifyContent: "space-between" }}>
        <EmbedTabs
          items={PRODUCTS_TABS.map((tab) => ({ id: tab.id, label: tab.label }))}
          value={activeTab}
          onChange={setActiveTab}
          aria-label="Product sections"
        />
        <div style={{ flex: "1 1 200px", minWidth: 180, maxWidth: 320 }}>
          <EmbedInput
            type="search"
            aria-label="Search your products"
            placeholder="Search your products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {activeTab === "subscriptions" && !canSubscriptions ? (
        <EmbedCard>
          <div role="status" data-testid="products-no-subscriptions" style={{ textAlign: "center", padding: t.spacing.lg }}>
            <EmbedText tone="secondary">{PRODUCTS_NO_SUBSCRIPTIONS_MESSAGE}</EmbedText>
          </div>
        </EmbedCard>
      ) : activeTab === "subscriptions" ? (
        rowCount === 0 ? (
          <EmbedCard>
            <div role="status" style={{ textAlign: "center", padding: t.spacing.lg }}>
              <EmbedText tone="secondary">No products match your filters.</EmbedText>
            </div>
          </EmbedCard>
        ) : (
          <SubscriptionsTable
            rows={subscriptionRows}
            buyAgainStateFor={(productId) => buyAgainState[productId]}
            onBuyAgain={handleBuyAgain}
            rmaStatusFor={rmaStatusFor}
            onRequestReturn={onRequestReturn}
            onOpenDetails={(item, trigger) => setDetail({ key: itemKey(item), trigger })}
            resumeStateFor={(id) => resumeState[id]}
            onResume={(id) => {
              const sub = subscriptions.find((s) => s.id === id);
              if (sub) void handleResume(sub);
            }}
            storesPermitted={access.stores}
            returnsPermitted={access.returns}
          />
        )
      ) : rowCount === 0 ? (
        <EmbedCard>
          <div role="status" style={{ textAlign: "center", padding: t.spacing.lg }}>
            <EmbedText tone="secondary">No products match your filters.</EmbedText>
          </div>
        </EmbedCard>
      ) : (
        <OwnedProductsTable
          rows={ownedRows}
          buyAgainStateFor={(productId) => buyAgainState[productId]}
          onBuyAgain={handleBuyAgain}
          rmaStatusFor={rmaStatusFor}
          onRequestReturn={onRequestReturn}
          onOpenDetails={(item, trigger) => setDetail({ key: itemKey(item), trigger })}
          onMove={access.stores ? (item, trigger) => setMoveDialog({ productId: item.product.id, trigger }) : undefined}
          storesPermitted={access.stores}
          returnsPermitted={access.returns}
        />
      )}

      <MoveUnitsDialog
        item={moveItem}
        returnFocusTo={moveDialog?.trigger ?? null}
        onClose={() => setMoveDialog(null)}
        onMoved={handleMoved}
        onStale={() => {
          if (moveDialog) handleMoveStale(moveDialog.productId);
        }}
        onMaybeStale={() => void refresh()}
      />

      <OwnedItemDetailDialog
        item={detailItem}
        returnFocusTo={detail?.trigger ?? null}
        pending={detailItem?.subscription ? pendingSubs.has(detailItem.subscription.id) : false}
        onClose={() => setDetail(null)}
        onCancelSubscription={(subscriptionId) => {
          // The detail dialog hands over to the existing cancel confirmation
          // (one modal at a time); cancellation is unavailable in this embed.
          setDetail(null);
          handleRequestCancel(subscriptionId);
        }}
      />

      <EmbedDialog
        open={confirmingSubscription != null}
        onClose={() => {
          if (confirmingSubscription) handleDismissCancel(confirmingSubscription.id);
        }}
        title="Cancel your renewal?"
        role="alertdialog"
        size="small"
        footer={
          <EmbedButton type="button" variant="primary" onClick={() => {
            if (confirmingSubscription) handleDismissCancel(confirmingSubscription.id);
          }}>Keep subscription</EmbedButton>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
          <EmbedText tone="secondary" size="xs">
            Only the account owner can cancel, using a code sent to their verified mobile or a registered passkey.
            Owner verification is not available in this embedded account view yet. Your subscription remains active.
          </EmbedText>
        </div>
      </EmbedDialog>
    </div>
  );
}
